/**
 * Note Table of Contents
 *
 * Minimal line-based TOC with popover on hover
 * - Shows horizontal lines for each section (one line per section)
 * - Active section highlighted with darker line
 * - Hover shows popover with full section list
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { NoteSection } from '@features/notes';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useNoteTOC } from '../hooks';
import { toggleCopySectionSelection } from '../store/chatSlice';
import { selectSelectedSectionIdsForCopy } from '../store/selectors';

import { NoteTOCPopover } from './NoteTOC/NoteTOCPopover';
import { NoteTOCSectionList } from './NoteTOC/NoteTOCSectionList';

export interface NoteTOCProps {
  /** Array of note sections to display */
  sections: NoteSection[];
  /** CSS selector for the scrollable container containing sections */
  containerSelector?: string;
  /** Handler for copying selected sections */
  onCopySelected?: () => void;
  /** Note ID for analytics tracking */
  noteId?: string;
  /** Encounter ID for analytics tracking */
  encounterId?: string;
}

/**
 * Table of Contents component with minimal line indicators
 *
 * Features:
 * - Narrow column with horizontal lines (one per section)
 * - Active section has darker line
 * - Hover shows popover with full section names
 * - Click to navigate to sections
 */
export const NoteTOC: React.FC<NoteTOCProps> = ({
  sections,
  containerSelector,
  onCopySelected,
  noteId,
  encounterId,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { noteSection } = useEventTracking();
  const selectedSectionIds = useAppSelector(selectSelectedSectionIdsForCopy);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Convert to TOC format and get active tracking
  const tocSections = useMemo(
    () =>
      sections.map(s => ({
        section_id: s.section_id,
        name: s.name,
      })),
    [sections],
  );

  const { activeSectionId, scrollToSection } = useNoteTOC(tocSections, containerSelector);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    // Delay closing to allow moving into popover
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 150);
  };

  const handlePopoverEnter = () => {
    // Clear any pending close timeout when entering popover
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handlePopoverLeave = () => {
    // Close immediately when leaving popover
    setAnchorEl(null);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleSectionClick = (sectionId: string) => {
    scrollToSection(sectionId);
    setAnchorEl(null);
  };

  const handleCopySelected = () => {
    onCopySelected?.();
  };

  if (sections.length === 0) {
    return null;
  }

  const open = Boolean(anchorEl);

  return (
    <>
      {/* Narrow column with line indicators */}
      <Box
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          width: 60,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          bgcolor: 'transparent',
          py: 2,
        }}
      >
        {sections.map(section => {
          const isActive = section.section_id === activeSectionId;
          return (
            <Box
              key={section.section_id}
              sx={{
                width: 20,
                height: isActive ? 3 : 2,
                bgcolor: isActive ? '#37352F' : '#E0E0E0',
                borderRadius: 0.5,
                transition: theme.transitions.create(['background-color', 'height'], {
                  duration: theme.transitions.duration.short,
                }),
                cursor: 'pointer',
              }}
              onClick={() => handleSectionClick(section.section_id)}
            />
          );
        })}
      </Box>

      {/* Popover with section list */}
      <NoteTOCPopover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onMouseEnter={handlePopoverEnter}
        onMouseLeave={handlePopoverLeave}
      >
        <NoteTOCSectionList
          sections={sections}
          activeSectionId={activeSectionId}
          selectedSectionIds={selectedSectionIds}
          onSectionClick={handleSectionClick}
          onToggleSelection={sectionId => dispatch(toggleCopySectionSelection(sectionId))}
          onCopySelected={handleCopySelected}
          noteId={noteId}
          encounterId={encounterId}
          noteSection={noteSection}
        />
      </NoteTOCPopover>
    </>
  );
};
